import { createApiRef } from '@backstage/frontend-plugin-api';

export interface Repository {
  name: string;
  slug: string;
  type: 'application' | 'infrastructure';
}

export interface UnapprovedPullRequest {
  id: number;
  repository: string;
  prNumber: number;
  mergedAt: string;
  title: string;
  authorLogin: string | null;
  url: string;
  hasApproval: boolean;
  approvedOnlyByAiReviewer: boolean;
  isBot: boolean;
}

export interface SyncStatus {
  repository: string;
  lastSynced: string | null;
  lastAttempted: string | null;
  lastSuccessful: string | null;
  firstFailed: string | null;
  statusChangedAt: string | null;
  defaultBranch: string | null;
  prCount: number;
  status: string;
  errorMessage: string | null;
}

export interface ComplianceStatus {
  repository: string;
  status: 'ready' | 'manually_excluded' | 'excluded' | 'error';
  errorMessage: string | null;
  observedAt: string;
}

export interface GlobalSyncStatus {
  status: 'never' | 'running' | 'success' | 'failed';
  lastStarted: string | null;
  lastCompleted: string | null;
  lastSuccessful: string | null;
  firstFailed: string | null;
  statusChangedAt: string | null;
  errorMessage: string | null;
}

export interface SyncRequest {
  status: 'accepted';
  requestedAt: string;
}

export interface GenerateResult {
  synced: number;
  unchanged?: number;
  /** @deprecated Rolling-deployment compatibility with the previous API. */
  skipped?: number;
  failed: number;
  total: number;
  errors?: Array<{ repository: string; error: string }>;
}

export interface ReportGenerationJob {
  id: string;
  from: string;
  to: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  result?: GenerateResult;
  error?: string;
}

export interface Soc2ReportApi {
  getRepositories(): Promise<{
    repositories: Repository[];
    count: number;
  }>;
  getUnapprovedPRs(since?: string): Promise<{
    pullRequests: UnapprovedPullRequest[];
    count: number;
  }>;
  getSyncStatus(): Promise<{
    syncStatus: SyncStatus[];
    complianceStatus: ComplianceStatus[];
    globalSyncStatus: GlobalSyncStatus;
  }>;
  triggerSync(): Promise<SyncRequest>;
  generateReport(from: string, to: string): Promise<ReportGenerationJob>;
  getReportJob(id: string): Promise<ReportGenerationJob>;
  downloadCsv(type: string, from: string, to: string): Promise<void>;
}

export const soc2ReportApiRef = createApiRef<Soc2ReportApi>().with({
  id: 'plugin.soc2-report.api',
});
